type FormatterKey = string;

const formatterCache = new Map<FormatterKey, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;

  const created = new Intl.DateTimeFormat(locale, options);
  formatterCache.set(key, created);
  return created;
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

const CLOCK_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};
const SHORT_TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short', hourCycle: 'h23' };
const DAY_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};
const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
const MERIDIEM_TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };

const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;
const RELATIVE_DATE_THRESHOLD_IN_HOURS = 48;

/**
 * `hh:mm:ss` on a 24-hour clock. The hour cycle is pinned because a US-locale
 * browser would otherwise render `01:05:07 PM` where the product asks for `13:05:07`.
 *
 * @param timestamp - Epoch milliseconds; 0 or negative renders an empty string.
 * @param locale - BCP 47 locale tag the caller pins, never the browser's.
 * @returns Formatted clock time, or an empty string.
 */
export function fvFormatClock(timestamp: number, locale: string): string {
  return timestamp > 0 ? formatter(locale, CLOCK_OPTIONS).format(new Date(timestamp)) : '';
}

/**
 * `hh:mm` on a 24-hour clock.
 *
 * @param value - Date, ISO string or epoch milliseconds.
 * @param locale - BCP 47 locale tag.
 * @returns Formatted time.
 */
export function fvFormatShortTime(value: Date | string | number, locale: string): string {
  return formatter(locale, SHORT_TIME_OPTIONS).format(toDate(value));
}

/**
 * Medium date plus short time, for timestamps that are not from today.
 *
 * @param value - Date, ISO string or epoch milliseconds.
 * @param locale - BCP 47 locale tag.
 * @returns Formatted date and time.
 */
export function fvFormatDateTime(value: Date | string | number, locale: string): string {
  return formatter(locale, DATE_TIME_OPTIONS).format(toDate(value));
}

/**
 * Day and month plus time — the compact dated form used inside report windows.
 *
 * @param value - Date, ISO string or epoch milliseconds.
 * @param locale - BCP 47 locale tag.
 * @returns Formatted day and time.
 */
export function fvFormatDayTime(value: Date | string | number, locale: string): string {
  return formatter(locale, DAY_TIME_OPTIONS).format(toDate(value));
}

/**
 * Long calendar date, e.g. `14 July 2026`.
 *
 * @param value - Date, ISO string or epoch milliseconds.
 * @param locale - BCP 47 locale tag.
 * @returns Formatted date.
 */
export function fvFormatLongDate(value: Date | string | number, locale: string): string {
  return formatter(locale, LONG_DATE_OPTIONS).format(toDate(value));
}

/**
 * Time with an upper-cased 12-hour meridiem, e.g. `2:55 PM`.
 *
 * @param value - Date, ISO string or epoch milliseconds.
 * @param locale - BCP 47 locale tag.
 * @returns Formatted time with meridiem.
 */
export function fvFormatMeridiemTime(value: Date | string | number, locale: string): string {
  return formatter(locale, MERIDIEM_TIME_OPTIONS).format(toDate(value)).toUpperCase();
}

/**
 * Same day → `hh:mm`; otherwise the date as well, because "since 09:14" three days
 * later is misleading.
 *
 * @param timestamp - Epoch milliseconds; 0 or negative renders an empty string.
 * @param locale - BCP 47 locale tag.
 * @param now - Reference instant, so the label ages with the caller's clock.
 * @returns Formatted timestamp, or an empty string.
 */
export function fvFormatSince(timestamp: number, locale: string, now: number): string {
  if (timestamp <= 0) return '';

  const then = new Date(timestamp);
  const isToday = new Date(now).toDateString() === then.toDateString();
  return isToday ? fvFormatShortTime(then, locale) : fvFormatDateTime(then, locale);
}

/**
 * Age of a timestamp in words, falling back to an absolute date past two days.
 *
 * @param timestamp - Epoch milliseconds; 0 or negative renders an empty string.
 * @param locale - BCP 47 locale tag.
 * @param now - Reference instant.
 * @returns Relative label, or an empty string.
 */
export function fvFormatRelative(timestamp: number, locale: string, now: number): string {
  if (timestamp <= 0) return '';

  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < SECONDS_IN_MINUTE) return `${seconds}s ago`;

  const minutes = Math.round(seconds / SECONDS_IN_MINUTE);
  if (minutes < MINUTES_IN_HOUR) return `${minutes} min ago`;

  const hours = Math.round(minutes / MINUTES_IN_HOUR);
  return hours < RELATIVE_DATE_THRESHOLD_IN_HOURS ? `${hours} h ago` : fvFormatDateTime(timestamp, locale);
}

/**
 * One outage slot. The plain `09:14–09:22` is only honest when both ends fall on
 * today — yesterday's `18:34–18:41` without a date reads as this morning. Any end
 * on another day gets the dated form, which also keeps a slot crossing midnight
 * from reading as time running backwards.
 *
 * @param startedAt - Epoch milliseconds the outage started.
 * @param endedAt - Epoch milliseconds it ended; 0 while it is still open.
 * @param ongoingLabel - Caller-supplied copy for an open window.
 * @param locale - BCP 47 locale tag.
 * @param now - Reference instant deciding what "today" means.
 * @returns Formatted window.
 */
export function fvFormatOutageWindow(
  startedAt: number,
  endedAt: number,
  ongoingLabel: string,
  locale: string,
  now: number
): string {
  const today = new Date(now).toDateString();
  const isToday = (timestamp: number): boolean => new Date(timestamp).toDateString() === today;

  if (!endedAt) {
    const start = isToday(startedAt) ? fvFormatShortTime(startedAt, locale) : fvFormatDayTime(startedAt, locale);
    return `${start} ${ongoingLabel}`;
  }
  if (isToday(startedAt) && isToday(endedAt)) {
    return `${fvFormatShortTime(startedAt, locale)}–${fvFormatShortTime(endedAt, locale)}`;
  }
  return `${fvFormatDayTime(startedAt, locale)} – ${fvFormatDayTime(endedAt, locale)}`;
}
